import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useInView, animate } from "framer-motion";
import { Check, ArrowRight, CalendarDays, Zap } from "lucide-react";
import { fmt } from "@/components/shared";

const ease = [0.22, 1, 0.36, 1];

export function PlansBackdrop() {
  const reduce = useReducedMotion();
  const particles = Array.from({ length: 14 }, (_, i) => ({ l: (i * 37 + 11) % 100, d: 14 + (i % 5) * 4, delay: -(i * 2.3), s: 2 + (i % 3) }));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden -z-0" aria-hidden="true" data-testid="plans-backdrop">
      <div className={`absolute inset-[-50%] plans-grid ${reduce ? "" : "plans-grid-move"}`} />
      <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[640px] h-[320px] rounded-full bg-cyan-500/10 blur-[110px] plans-glow" />
      <div className="absolute right-[-10%] bottom-[-10%] w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-[120px]" />
      {!reduce && particles.map((p, i) => (
        <span key={i} className={`plans-particle ${i > 6 ? "hidden md:block" : ""}`} style={{ left: `${p.l}%`, width: p.s, height: p.s, animationDuration: `${p.d}s`, animationDelay: `${p.delay}s` }} />
      ))}
    </div>
  );
}

export function PlansHero({ eyebrow, title, subtitle }) {
  const reduce = useReducedMotion();
  const fade = (delay) => reduce ? {} : { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, ease, delay } };
  return (
    <div className="relative text-center max-w-3xl mx-auto mb-14" data-testid="plans-hero">
      <motion.p {...fade(0)} className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">{eyebrow}</motion.p>
      <div className="relative inline-block mt-3">
        <span className="absolute inset-x-8 -inset-y-2 rounded-full bg-cyan-400/15 blur-2xl plans-glow" aria-hidden="true" />
        <motion.h1 {...fade(0.12)} className="relative font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{title}</motion.h1>
      </div>
      <motion.p {...fade(0.28)} className="text-slate-400 mt-5 text-base md:text-lg">{subtitle}</motion.p>
    </div>
  );
}

export function CountUp({ value, decimals = 0, className = "", testId }) {
  const ref = useRef(null);
  const prev = useRef(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const from = prev.current;
    const ctrl = animate(from, value, { duration: 0.9, ease, onUpdate: (v) => { node.textContent = decimals ? fmt(v) : Math.round(v).toLocaleString(); } });
    prev.current = value;
    return () => ctrl.stop();
  }, [value, decimals]);
  return <span ref={ref} className={className} data-testid={testId}>{decimals ? fmt(value) : value}</span>;
}

export function WorkingDaysBar({ done = 0, total = 200, testId = "working-days-bar", compact = false }) {
  const pct = Math.min(100, (done / Math.max(1, total)) * 100);
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3 text-cyan-400 plans-icon-tick" /> Working days</span>
        <span className="text-slate-300"><CountUp value={done} /> / {total}</span>
      </div>
      <div className={`relative w-full ${compact ? "h-1.5" : "h-2"} rounded-full bg-slate-800/80 overflow-hidden`}>
        <motion.div className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-cyan-300 to-blue-500 plans-bar-sheen"
          initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease }} />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-slate-600 mt-1"><span>0</span><span>{total}</span></div>
    </div>
  );
}

export function PlanCard({ plan, index = 0, highlight = false, cta, activeDays = null }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [hover, setHover] = useState(false);
  const total = plan.duration_days || 200;
  return (
    <motion.div ref={ref} data-testid={`plan-card-${plan.id}`}
      initial={reduce ? false : { opacity: 0, y: 28, scale: 0.97 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.7, ease, delay: Math.min(index * 0.12, 0.7) }}
      whileHover={reduce ? {} : { y: -6 }}
      onHoverStart={() => setHover(true)} onHoverEnd={() => setHover(false)}
      className={`plan-card group relative rounded-2xl p-7 bg-[#0B1224]/80 backdrop-blur-xl border transition-[background-color,border-color,box-shadow] duration-500 ${highlight ? "border-cyan-500/40" : "border-cyan-500/15"} hover:border-cyan-400/60 hover:bg-[#0E1730]/90 hover:shadow-[0_20px_60px_-15px_rgba(0,200,255,0.35)]`}>
      <span className="plan-card-border" aria-hidden="true" />
      {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-cyan-400 text-slate-950 text-xs font-bold">Popular</span>}
      <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">{plan.name}</p>
      <div className="mt-4 flex items-end gap-2">
        <motion.span animate={{ scale: hover && !reduce ? 1.04 : 1 }} transition={{ duration: 0.4, ease }} className="font-mono text-4xl font-bold text-white origin-left inline-block">{plan.daily_percentage}%</motion.span>
        <span className="text-slate-400 mb-1">per working day</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="plans-roi-chip inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-cyan-200 border border-cyan-400/30 bg-cyan-500/10" data-testid="roi-highlight"><Zap className="w-3 h-3" /> Up to {plan.daily_percentage}% Daily</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-slate-200 border border-white/10 bg-white/5" data-testid="days-highlight"><CalendarDays className="w-3 h-3 text-cyan-300 plans-icon-tick" /> {total} Working Days</span>
      </div>
      <ul className="mt-6 space-y-3 text-sm text-slate-300">
        {[`${plan.daily_percentage}% of principal per working day (Mon–Fri)`, `${total} working days · max ROI ${Math.round(plan.daily_percentage * total)}% (non-compounding)`, `Min ${fmt(plan.min_amount)} / Max ${fmt(plan.max_amount)} USDT`, "Principal returned at maturity · full ledger transparency"].map((t, i) => (
          <motion.li key={i} initial={reduce ? false : { opacity: 0, x: -8 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.5, ease, delay: 0.25 + i * 0.08 + Math.min(index * 0.12, 0.7) }} className="flex gap-2">
            <Check className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" /> {t}
          </motion.li>
        ))}
      </ul>
      <div className="mt-6"><WorkingDaysBar done={activeDays ?? 0} total={total} testId={`plan-progress-${plan.id}`} compact /></div>
      <div className="mt-6">{cta}</div>
    </motion.div>
  );
}

export function GlowCta({ children = "Get Started", onClick, to, as: Comp = "button", testId, className = "", ...rest }) {
  const props = to ? { to } : { onClick, type: "button" };
  return (
    <Comp {...props} {...rest} data-testid={testId}
      className={`plans-cta btn-glow group/cta w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-slate-950 transition-[transform,filter,box-shadow] duration-300 hover:scale-[1.02] hover:brightness-110 hover:shadow-[0_0_28px_rgba(0,240,255,0.45)] active:scale-[0.99] ${className}`}>
      {children}
      <span className="relative inline-flex w-5 h-4 overflow-hidden">
        <ArrowRight className="w-4 h-4 absolute left-0 transition-transform duration-300 group-hover/cta:translate-x-2" />
        <ArrowRight className="w-4 h-4 absolute -left-4 transition-transform duration-300 group-hover/cta:translate-x-4 opacity-0 group-hover/cta:opacity-100" />
      </span>
    </Comp>
  );
}
